/* ==========================================================================
   Apus Ami ĐỨNG + BAY VÀO — chép vũ đạo mascot web-marketing (`@apus/ami` MascotChoreography/useMascotEntrance,
   bản DOM thuần ở apus-ami-widget/src/widget.js D-15/D-17), rút gọn cho widget nghiệp vụ:

   · Mỗi PHIÊN trình duyệt một lần (sessionStorage), 2,4 s sau khi trang mở: cổng Apus Portal hiện ở góc trái
     dưới → mở → chim bay theo đường bezier (6 khung cánh, vệt sáng) → đáp góc phải dưới → đứng.
   · Chim đứng có bong bóng chào; ⛔ ai chạm trong AUTO_COLLAPSE_MS ⇒ gập vào nút tròn 56 px (nút nổi sẵn có).
     Rê chuột / focus vào chim thì giữ lại. Đã gập (hoặc đã mở hội thoại) thì cả phiên giữ nút tròn — chim
     đứng mãi tốn diện tích màn hình nghiệp vụ.
   · Màn nhỏ: ⛔ màn vào, vào thẳng nút tròn. `prefers-reduced-motion`: ⛔ bay, chim hiện đứng rồi gập.
   Thời lượng các pha chép NGUYÊN hằng số bản React (owner đã duyệt bên web-marketing).
   ========================================================================== */
import { el } from "./dom.ts";

const ENTRANCE_KEY = "apx:ami-entrance:v1";
const COMPACT_KEY = "apx:ami-compact:v1";
const LOAD_DELAY_MS = 2400;
const WARMUP_TIMEOUT_MS = 12000;
const PORTAL_FORM_MS = 1500;
const PORTAL_OPEN_MS = 700;
const FLIGHT_MS = 3280;
const LANDING_MS = 1850;
const HANDOFF_MS = 440;
/** Chim đứng bao lâu rồi tự gập (owner 2026-10-20: "đứng 5–10 s nếu không dùng thì thu gọn"). */
export const AUTO_COLLAPSE_MS = 8000;
const SMALL_VIEWPORT = "(max-width: 767px), (pointer: coarse) and (max-height: 480px)";
/* tâm khung bay ⇄ tâm chim đứng lệch nhau trong ảnh gốc (bản React) */
const FX = 734 / 1254 - 206 / 384;
const FY = 42 / 384 - 81 / 1254;

type Phase = "boot" | "portal-forming" | "portal-opening" | "flight" | "landing" | "crossfade";
type Scene = "off" | "entrance" | "standing" | "compact";

const store = {
  get(k: string): string | null {
    try {
      return window.sessionStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(k: string, v: string | null): void {
    try {
      if (v === null) window.sessionStorage.removeItem(k);
      else window.sessionStorage.setItem(k, v);
    } catch {
      /* chế độ riêng tư / bị chặn: chạy như phiên mới */
    }
  },
};
const mq = (q: string): boolean => {
  try {
    return !!window.matchMedia?.(q).matches;
  } catch {
    return false;
  }
};
const bez = (p0: number, p1: number, p2: number, p3: number, t: number) => {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
};
const bezD = (p0: number, p1: number, p2: number, p3: number, t: number) => {
  const u = 1 - t;
  return 3 * u * u * (p1 - p0) + 6 * u * t * (p2 - p1) + 3 * t * t * (p3 - p2);
};
const smooth = (t: number) => t * t * (3 - 2 * t);
function frameAt(ms: number): number {
  if (ms < 280) return 0;
  if (ms < 520) return 1;
  if (ms < FLIGHT_MS - 620) return [1, 3, 2, 3][Math.floor((ms - 520) / 120) % 4] ?? 1;
  if (ms < FLIGHT_MS - 260) return 4;
  return 5;
}

export interface PetOptions {
  /** `.apx-root` của widget — nơi gắn chim và lớp màn vào; thuộc tính `data-pet` điều khiển nút tròn. */
  root: HTMLElement;
  /** thư mục `…/assets/ami/pet/` */
  base: string;
  title: string;
  greeting: string;
  status: string;
  onOpen(): void;
}

export class AmiPet {
  private readonly o: PetOptions;
  private scene: Scene = "off";
  private phase: Phase = "boot";
  private timers: number[] = [];
  private collapseTimer = 0;
  private raf = 0;
  private hold = { hover: false, focus: false };
  private shell: HTMLElement | null = null;
  private typed: HTMLElement | null = null;
  private entrance: HTMLElement | null = null;
  private parts: { stage: HTMLElement; portal: HTMLElement; landed: HTMLElement; flyer: HTMLElement; frames: HTMLElement[]; trail: SVGSVGElement; glow: SVGPathElement; core: SVGPathElement; grad: SVGLinearGradientElement } | null = null;
  private observer: MutationObserver | null = null;

  constructor(o: PetOptions) {
    this.o = o;
  }

  start(): void {
    const root = this.o.root;
    this.observer = new MutationObserver(() => {
      if (root.dataset.open === "true" && this.scene !== "compact") this.collapse(false);
    });
    this.observer.observe(root, { attributes: true, attributeFilter: ["data-open"] });
    if (mq(SMALL_VIEWPORT) || store.get(COMPACT_KEY) === "true" || root.dataset.open === "true") return this.setScene("compact");
    this.buildShell();
    if (store.get(ENTRANCE_KEY) || mq("(prefers-reduced-motion: reduce)")) return this.stand();
    this.setScene("entrance");
    this.later(() => void this.warmThenEnter(), LOAD_DELAY_MS);
  }

  /** Xem lại màn bay vào (cho buổi demo): bỏ cờ phiên, gập panel đã do widget lo. */
  replay(): void {
    store.set(ENTRANCE_KEY, null);
    store.set(COMPACT_KEY, null);
    this.clear();
    this.entrance?.remove();
    this.entrance = null;
    this.parts = null;
    if (!this.shell) this.buildShell();
    this.setScene("entrance");
    if (mq("(prefers-reduced-motion: reduce)")) return this.stand();
    void this.warmThenEnter();
  }

  destroy(): void {
    this.clear();
    this.observer?.disconnect();
    this.shell?.remove();
    this.entrance?.remove();
    delete this.o.root.dataset.pet;
  }

  /* ------------------------------------------------------------ dựng */
  private img(cls: string, name: string): HTMLImageElement {
    return el("img", cls, { src: this.o.base + name, alt: "", draggable: "false" }) as HTMLImageElement;
  }

  private buildShell(): void {
    const shell = el("div", "apx-petshell");
    const btn = el("button", "apx-pet", { type: "button", "aria-label": "Mở " + this.o.title, "aria-haspopup": "dialog" });
    const cue = el("span", "apx-pet-cue", { "aria-hidden": "true" });
    const title = el("span", "apx-pet-cue-title");
    title.textContent = this.o.title;
    const status = el("span", "apx-pet-cue-status");
    const ghost = el("span", "apx-pet-cue-ghost");
    ghost.textContent = this.o.greeting;
    const typed = el("span", "apx-pet-cue-typed");
    typed.textContent = this.o.status;
    status.append(ghost, typed);
    cue.append(title, status);
    const stage = el("span", "apx-pet-stage", { "aria-hidden": "true" });
    const rig = el("span", "apx-pet-rig");
    rig.append(this.img("apx-pet-layer", "body.webp"), this.img("apx-pet-layer apx-pet-wing", "wing.webp"), this.img("apx-pet-layer apx-pet-eyes", "eyes-closed.webp"));
    stage.append(el("span", "apx-pet-ground"), rig);
    btn.append(cue, stage);
    const fold = el("button", "apx-pet-fold", { type: "button", "aria-label": "Thu gọn Apus Ami", title: "Thu gọn" });
    fold.textContent = "–";
    shell.append(btn, fold);
    btn.addEventListener("click", () => this.o.onOpen());
    fold.addEventListener("click", () => this.collapse(true));
    /* giữ chim đứng khi người dùng đang ở trên nó (bản React `useMascotAutoCollapse`) */
    shell.addEventListener("pointermove", () => this.setHold("hover", true));
    shell.addEventListener("pointerleave", () => this.setHold("hover", false));
    shell.addEventListener("focusin", () => this.setHold("focus", true));
    shell.addEventListener("focusout", (e) => {
      if (!shell.contains((e as FocusEvent).relatedTarget as Node | null)) this.setHold("focus", false);
    });
    this.typed = typed;
    this.shell = shell;
    this.o.root.append(shell);
  }

  private buildEntrance(): void {
    const ns = "http://www.w3.org/2000/svg";
    const node = el("div", "apx-entrance", { "aria-hidden": "true" });
    const stage = el("span", "apx-entrance-stage");
    stage.append(el("span", "apx-entrance-aura"));
    const portal = el("span", "apx-entrance-portal");
    portal.append(this.img("apx-entrance-fill", "entrance/portal.webp"));
    const wordmark = el("span", "apx-entrance-wordmark");
    wordmark.append(this.img("apx-entrance-fill", "entrance/portal-wordmark.webp"));
    const trail = document.createElementNS(ns, "svg");
    trail.setAttribute("class", "apx-entrance-trail");
    const defs = document.createElementNS(ns, "defs");
    const grad = document.createElementNS(ns, "linearGradient");
    grad.id = "apx-trail-" + Math.random().toString(36).slice(2, 8);
    grad.setAttribute("gradientUnits", "userSpaceOnUse");
    const stops: Array<[string, string, string]> = [["0", "#d9b13b", "0.12"], ["0.58", "#eed9a3", "1"], ["1", "#d9b13b", "0.9"]];
    for (const [off, color, op] of stops) {
      const st = document.createElementNS(ns, "stop");
      st.setAttribute("offset", off);
      st.setAttribute("stop-color", color);
      st.setAttribute("stop-opacity", op);
      grad.append(st);
    }
    defs.append(grad);
    const glow = document.createElementNS(ns, "path");
    glow.setAttribute("class", "apx-entrance-trail-glow");
    const core = document.createElementNS(ns, "path");
    core.setAttribute("class", "apx-entrance-trail-core");
    core.setAttribute("stroke", "url(#" + grad.id + ")");
    trail.append(defs, glow, core);
    const flyer = el("span", "apx-entrance-flyer");
    const frames = [1, 2, 3, 4, 5, 6].map((i) => this.img("apx-entrance-fill apx-entrance-frame" + (i === 1 ? " apx-on" : ""), "entrance/flight-" + i + ".webp"));
    flyer.append(...frames);
    const landed = el("span", "apx-entrance-landed");
    landed.append(el("span", "apx-entrance-shadow"), this.img("apx-entrance-fill", "body.webp"), this.img("apx-entrance-fill", "wing.webp"));
    stage.append(portal, wordmark, trail, flyer, landed, el("span", "apx-entrance-ripple"));
    node.append(stage);
    this.o.root.append(node);
    this.entrance = node;
    this.parts = { stage, portal, landed, flyer, frames, trail, glow, core, grad };
  }

  /* ------------------------------------------------------------ máy trạng thái */
  private setScene(s: Scene): void {
    this.scene = s;
    this.o.root.dataset.pet = s;
  }

  private setPhase(p: Phase): void {
    this.phase = p;
    this.entrance?.setAttribute("data-phase", p);
  }

  private later(fn: () => void, ms: number): void {
    this.timers.push(window.setTimeout(fn, ms));
  }

  private clear(): void {
    this.timers.forEach((t) => window.clearTimeout(t));
    this.timers = [];
    window.clearTimeout(this.collapseTimer);
    this.collapseTimer = 0;
    window.cancelAnimationFrame(this.raf);
  }

  /** Nạp sẵn mọi khung trước khi diễn (mạng chậm ⇒ bỏ màn vào, đứng luôn — ⛔ để chim bay giật). */
  private async warmThenEnter(): Promise<void> {
    const names = ["entrance/portal.webp", "entrance/portal-wordmark.webp", ...[1, 2, 3, 4, 5, 6].map((i) => "entrance/flight-" + i + ".webp"), "body.webp", "wing.webp", "eyes-closed.webp"];
    const load = (n: string) => new Promise<void>((ok, bad) => {
      const i = new Image();
      i.onload = () => ok();
      i.onerror = () => bad(new Error(n));
      i.src = this.o.base + n;
    });
    const ready = await Promise.race([Promise.all(names.map(load)).then(() => true, () => false), new Promise<boolean>((r) => window.setTimeout(() => r(false), WARMUP_TIMEOUT_MS))]);
    if (this.scene !== "entrance") return;
    if (!ready || document.visibilityState === "hidden") return this.stand();
    store.set(ENTRANCE_KEY, "1");
    this.buildEntrance();
    this.setPhase("portal-forming");
    this.later(() => {
      this.setPhase("portal-opening");
      this.later(() => {
        this.setPhase("flight");
        this.fly();
      }, PORTAL_OPEN_MS);
    }, PORTAL_FORM_MS);
  }

  /* đường bezier bậc ba từ MIỆNG CỔNG tới CHỖ ĐỨNG, tăng tốc smoothstep, nghiêng theo tiếp tuyến, vệt sáng de Casteljau */
  private fly(): void {
    const P = this.parts;
    if (!P) return;
    let elapsed = 0;
    let prev = performance.now();
    let frame = 0;
    const place = (raw: number) => {
      const sb = P.stage.getBoundingClientRect();
      const pb = P.portal.getBoundingClientRect();
      const lb = P.landed.getBoundingClientRect();
      const sx = pb.left - sb.left + pb.width * 0.56;
      const sy = pb.top - sb.top + pb.height * 0.54;
      const tx = lb.left - sb.left + lb.width / 2 + P.flyer.offsetWidth * FX;
      const ty = lb.top - sb.top + lb.height / 2 + P.flyer.offsetHeight * FY;
      const p = smooth(raw);
      const lift = Math.min(270, Math.max(150, sb.height * 0.3));
      const apex = Math.max(24, Math.min(sy, ty) - lift);
      const c1x = sx + (tx - sx) * 0.3;
      const c2x = sx + (tx - sx) * 0.7;
      const c1y = apex + 18;
      const c2y = apex;
      const x = bez(sx, c1x, c2x, tx, p);
      const y = bez(sy, c1y, c2y, ty, p);
      const tp = Math.min(1, Math.max(0, p - 0.025));
      const lerp = (a: number, b: number) => a + (b - a) * tp;
      const [p01x, p01y, p12x, p12y, p23x, p23y] = [lerp(sx, c1x), lerp(sy, c1y), lerp(c1x, c2x), lerp(c1y, c2y), lerp(c2x, tx), lerp(c2y, ty)];
      const [p012x, p012y, p123x, p123y] = [lerp(p01x, p12x), lerp(p01y, p12y), lerp(p12x, p23x), lerp(p12y, p23y)];
      const ex = lerp(p012x, p123x);
      const ey = lerp(p012y, p123y);
      const d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} C ${p01x.toFixed(1)} ${p01y.toFixed(1)}, ${p012x.toFixed(1)} ${p012y.toFixed(1)}, ${ex.toFixed(1)} ${ey.toFixed(1)}`;
      P.glow.setAttribute("d", d);
      P.core.setAttribute("d", d);
      P.grad.setAttribute("x1", String(sx));
      P.grad.setAttribute("y1", String(sy));
      P.grad.setAttribute("x2", String(ex));
      P.grad.setAttribute("y2", String(ey));
      P.trail.style.opacity = String(raw < 0.055 ? raw / 0.055 : Math.max(0, Math.min(1, (1 - raw) / 0.14)));
      const level = 1 - smooth(Math.max(0, (raw - 0.78) / 0.22));
      const angle = ((Math.atan2(bezD(sy, c1y, c2y, ty, p), bezD(sx, c1x, c2x, tx, p)) * 180) / Math.PI) * 0.56 * level;
      const scale = 0.58 + 0.42 * (1 - Math.pow(1 - p, 3));
      P.flyer.style.opacity = String(Math.min(1, raw / 0.045));
      P.flyer.style.transform = `translate3d(${x - P.flyer.offsetWidth / 2}px, ${y - P.flyer.offsetHeight / 2}px, 0) rotate(${angle}deg) scale(${scale})`;
    };
    const tick = (t: number) => {
      if (this.scene !== "entrance") return;
      const delta = document.visibilityState === "hidden" ? 0 : t - prev;
      prev = t;
      elapsed = Math.min(FLIGHT_MS, elapsed + Math.max(0, delta));
      place(elapsed / FLIGHT_MS);
      const next = frameAt(elapsed);
      if (next !== frame) {
        P.frames[frame]?.classList.remove("apx-on");
        P.frames[next]?.classList.add("apx-on");
        frame = next;
      }
      if (elapsed < FLIGHT_MS) this.raf = window.requestAnimationFrame(tick);
      else this.raf = window.requestAnimationFrame(() => this.land());
    };
    this.raf = window.requestAnimationFrame(tick);
  }

  private land(): void {
    this.setPhase("landing");
    this.later(() => {
      this.setPhase("crossfade");
      this.stand();
      this.later(() => {
        this.entrance?.remove();
        this.entrance = null;
        this.parts = null;
      }, HANDOFF_MS);
    }, LANDING_MS);
  }

  /** Chim ĐỨNG: gõ lời chào từng chữ, rồi hẹn giờ gập. */
  private stand(): void {
    if (!this.shell) this.buildShell();
    this.setScene("standing");
    const text = this.o.greeting;
    const t = this.typed;
    if (t) {
      t.textContent = "";
      t.parentElement?.setAttribute("data-greeting", "true");
      [...text].forEach((_, i) => this.later(() => (t.textContent = text.slice(0, i + 1)), 400 + i * 32));
    }
    this.armCollapse();
  }

  private setHold(k: "hover" | "focus", v: boolean): void {
    if (this.hold[k] === v) return;
    this.hold[k] = v;
    this.armCollapse();
  }

  private armCollapse(): void {
    window.clearTimeout(this.collapseTimer);
    this.collapseTimer = 0;
    if (this.scene !== "standing" || this.hold.hover || this.hold.focus) return;
    this.collapseTimer = window.setTimeout(() => this.collapse(false), AUTO_COLLAPSE_MS);
  }

  /** Gập vào nút tròn cho cả phiên. `byUser`: bấm nút "–" ⇒ chuyển tiêu điểm sang nút tròn (WCAG: tự gập ⛔ cướp tiêu điểm). */
  private collapse(byUser: boolean): void {
    if (this.scene === "compact") return;
    const focusOnPet = !!this.shell && this.shell.contains((this.o.root.getRootNode() as ShadowRoot).activeElement);
    this.clear();
    this.entrance?.remove();
    this.entrance = null;
    this.parts = null;
    store.set(COMPACT_KEY, "true");
    this.o.root.setAttribute("data-pet-anim", "");
    this.setScene("compact");
    window.setTimeout(() => this.o.root.removeAttribute("data-pet-anim"), 400);
    if (byUser || focusOnPet) (this.o.root.querySelector(".apx-launcher") as HTMLElement | null)?.focus();
  }
}
